import {
  Customer,
  phoneNumbers,
  PolicySummary,
  provincesMap,
  statesMap,
} from "shared";
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";

import { unstable__Button as Button } from "@hagerty/react-components";

import { EditMailingAddressForm } from "./form/EditMailingAddressForm";
import {
  areCustomersTheSame,
  caPostalCodeRegExp,
  caPostalCodeWithSpaceRegExp,
  navigateToSettings,
  saveCustomerInfo,
  usPostalCodeRegExp,
} from "../../utils";
import { useScrollToTop } from "../../../../hooks/useScrollToTop";
import {
  useIsStateFarmPartitioned,
  useUrls,
} from "../../../../app/Providers/AppContext";
import { usePolicySummaries } from "../../../../hooks/usePolicySummaries";
import { UpdateStatus, ValidationErrors } from "../../ContactInfo.types";
import { EditPageTemplate } from "../../EditPageTemplate";
import { mt } from "../../../../utils/tracking";
import { ContactInfoSkeleton } from "../../components";
import { useDialog } from "../../../Dialog";
import { DialogButtonGroupV2, DialogV2 } from "../../../DialogV2";
import {
  CustomerMailingAddress,
  CustomerMailingAddressKey,
} from "./EditMailingAddressPage.types";

export type EditMailingAddressPageProps = {
  initialCustomer: Customer;
};

/**
 * Edit the mailing address fields in contact information
 */
export const EditMailingAddressPage = ({
  initialCustomer,
}: EditMailingAddressPageProps) => {
  const isStateFarmPartitioned = useIsStateFarmPartitioned();

  if (isStateFarmPartitioned) {
    navigateToSettings();
    return <ContactInfoSkeleton testId="edit-mailing-address" rows={5} />;
  }

  useScrollToTop();

  const policies = usePolicySummaries();

  return (
    <EditMailingAddressPageWrapped
      initialCustomer={initialCustomer}
      policies={policies}
    />
  );
};

const EditMailingAddressPageWrapped = ({
  initialCustomer,
  policies,
}: EditMailingAddressPageProps & { policies: PolicySummary[] | undefined }) => {
  const [status, setStatus] = useState<UpdateStatus>("ok");
  const [disableDialogButtons, setDisableDialogButtons] = useState(false);
  const policyAddressDialog = useDialog();
  const urls = useUrls();
  const [address, setAddress] = useState(
    getMailingAddressFields(initialCustomer)
  );
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [changedFields, setChangedFields] = useState(
    new Set<CustomerMailingAddressKey>()
  );

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const hasApexPolicy = !!policies && policies.some((p) => p.source === "APEX");

  useEffect(() => {
    mt.trackView();
    setValidationErrors(getValidationErrors(address));
  }, []);

  return (
    <>
      <EditPageTemplate
        heading="Change your mailing address"
        message="This is where Hagerty will mail communications and documents."
        unsavedChangesDialogHeading="Save mailing address changes?"
        pending={status == "pending"}
        isFormDirty={checkIfDirty()}
        hasValidationErrors={hasValidationErrors}
        showMutateError={status === "mutateError"}
        onClickSave={onClickSave}
      >
        <EditMailingAddressForm
          address={address}
          changedFields={changedFields}
          validationErrors={validationErrors}
          pending={status == "pending"}
          onChange={onChange}
        />
      </EditPageTemplate>

      {policyAddressDialog.isOpen && (
        <DialogV2
          heading="Mailing address successfully changed."
          message="You may also need to update your insurance address, because premiums vary from one zip code to another. You'll have to do that separately."
          delayPopup
          onDismiss={() => false}
        >
          <DialogButtonGroupV2>
            <Button
              onClick={onClickPolicyAddressDialogNoThanks}
              variant="outline"
              size="small"
              disabled={disableDialogButtons}
            >
              No thanks
            </Button>
            <Button
              autoFocus
              as="a"
              target="_blank"
              href={
                hasApexPolicy
                  ? `tel:+${phoneNumbers.apex}`
                  : urls.account.changeAddress
              }
              onClick={onClickPolicyAddressDialogUpdate}
              variant="solid"
              size="small"
            >
              {hasApexPolicy
                ? `Call ${phoneNumbers.apex}`
                : "Update policy address"}
            </Button>
          </DialogButtonGroupV2>
        </DialogV2>
      )}
    </>
  );

  function onClickPolicyAddressDialogNoThanks() {
    setDisableDialogButtons(true);
    navigateToSettings(true);
  }

  function onClickPolicyAddressDialogUpdate(event: SyntheticEvent) {
    if (disableDialogButtons) {
      event.preventDefault();
      return;
    }

    setDisableDialogButtons(true);

    // setTimeout causes change address link to open in new tab before redirect happens
    setTimeout(() => navigateToSettings(true));
  }

  function getMailingAddressFields({
    line1,
    line2,
    city,
    state,
    postalCode,
    country,
  }: Customer): CustomerMailingAddress {
    const defaultState = country === "USA" ? statesMap.AL : provincesMap.AB;

    return {
      line1,
      line2,
      city,
      state: state.length ? state : defaultState,
      postalCode,
      country,
    };
  }

  function checkIfDirty(): boolean {
    return !areCustomersTheSame<CustomerMailingAddress>(
      initialCustomer,
      address
    );
  }

  function onChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const field = event.target.id as CustomerMailingAddressKey;
    const value = event.target.value;
    const updatedCustomer = { ...address, [field]: value };

    setAddress(updatedCustomer);
    setValidationErrors(getValidationErrors(updatedCustomer));
    setChangedFields(new Set(changedFields).add(field));
  }

  function getValidationErrors(
    customer: CustomerMailingAddress
  ): ValidationErrors {
    const usa = customer.country === "USA";
    const postalRegex = usa ? usPostalCodeRegExp : caPostalCodeRegExp;
    const errors: ValidationErrors = {};

    if (!customer.line1.trim()) {
      errors.line1 = "Please enter your street address";
    }

    if (!customer.city.trim()) {
      errors.city = "Please enter your city";
    }

    if (!customer.state) {
      errors.state = "Please select your state";
    }

    if (!RegExp(postalRegex).test(customer.postalCode.trim())) {
      errors.postalCode = `Please provide a valid ${
        usa ? "zip code" : "postal code"
      }`;
    }

    return errors;
  }

  async function onClickSave(): Promise<void> {
    mt.trackAction({ actionType: "button", actionName: "save" });

    setStatus("pending");

    try {
      await saveCustomerInfo({
        ...initialCustomer,
        ...trimCustomerMailingAddress(normalizePostalCode(address)),
      });
    } catch {
      setStatus("mutateError");
      return;
    }

    if (policies?.length) {
      setStatus("ok");
      policyAddressDialog.open();
    } else {
      navigateToSettings(true);
    }
  }

  function normalizePostalCode(
    customer: CustomerMailingAddress
  ): CustomerMailingAddress {
    const { country, postalCode } = customer;

    if (country === "USA") {
      return customer;
    }

    if (RegExp(caPostalCodeWithSpaceRegExp).test(postalCode)) {
      return customer;
    }

    const firstHalf = postalCode.substring(0, 3);
    const secondHalf = postalCode.substring(3);

    return { ...customer, postalCode: `${firstHalf} ${secondHalf}` };
  }

  function trimCustomerMailingAddress(
    customer: CustomerMailingAddress
  ): CustomerMailingAddress {
    return {
      ...customer,
      line1: customer.line1.trim(),
      line2: customer.line2.trim(),
      city: customer.city.trim(),
      state: customer.state.trim(),
      postalCode: customer.postalCode.trim(),
    };
  }
};
